import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Store} from '@ngrx/store';

import {WikiGroup} from '../models/wiki.model';
import {OphDialogFooterComponent} from './../../shared/design/oph-dialog-footer/oph-dialog-footer.component';
import {OphInputOrangeComponent} from './../../shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {
  CreateWikiGroupAction,
  DeleteWikiGroupAction,
  GetAllWikiGroupsAction,
  UpdateWikiGroupAction,
} from './../../core/store/wiki/wiki.action';

export type WikiGroupDialogData = {
  group?: WikiGroup;
  groups: WikiGroup[];
};

@Component({
  selector: 'wiki-group-dialog',
  standalone: true,
  imports: [OphInputOrangeComponent, FormsModule, ReactiveFormsModule, OphDialogFooterComponent, MatIconModule],
  templateUrl: './wiki-group-dialog.component.html',
  styleUrl: './wiki-group-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiGroupDialogComponent implements OnInit {
  readonly data = inject<WikiGroupDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<WikiGroupDialogData>);
  private store$ = inject(Store);
  private fb = inject(FormBuilder);

  public groupFG = this.fb.group({
    _id: this.data.group?._id || null,
    name: this.data.group?.name || '',
    items: this.fb.array(this.data.group?.items || []),
    order: this.data.group?.order === null ? this.data?.groups?.length + 1 : this.data.group?.order,
  });

  public menuOptions = ['delete'];

  isLoading = false;
  groupHasId = false;

  ngOnInit(): void {
    this.groupHasId = !!this.data.group?._id;
    this.groupFG = this.fb.group({
      _id: this.data.group?._id || null,
      name: this.data.group?.name || '',
      items: this.fb.array(this.data.group?.items || []),
      order: !this.data.group ? this.data?.groups?.length + 1 : this.data.group?.order,
    });
    this.groupFG.controls.name.setValidators(Validators.required);
  }

  footerActionLabels() {
    return {
      cancel: 'Cancel',
      save: this.groupHasId ? 'Update Group' : 'Create Group',
    };
  }

  onSave() {
    if (this.groupHasId) {
      this.updateWikiGroup();
      return;
    }
    this.createWikiGroup();
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMenuOption(type: (typeof this.menuOptions)[number]) {
    if (type === 'delete') {
      this.deleteWikiGroup();
    }
  }

  createWikiGroup(): void {
    if (this.groupFG.invalid) {
      this.groupFG.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new CreateWikiGroupAction({
        wikiGroup: this.groupFG.value as WikiGroup,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.groupFG.value);
          this.store$.dispatch(new GetAllWikiGroupsAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          console.error('Error creating wiki group:', error);
        },
      })
    );
  }

  updateWikiGroup(): void {
    if (this.groupFG.invalid) {
      this.groupFG.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.store$.dispatch(
      new UpdateWikiGroupAction({
        wikiGroup: this.groupFG.value as WikiGroup,
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.groupFG.value);
          this.store$.dispatch(new GetAllWikiGroupsAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          console.error('Error creating wiki group:', error);
        },
      })
    );
  }

  deleteWikiGroup() {
    this.isLoading = true;
    this.store$.dispatch(
      new DeleteWikiGroupAction({
        id: this.data.group?._id || '',
        onSuccess: () => {
          this.isLoading = false;
          this.dialogRef.close(this.groupFG.value);
          this.store$.dispatch(new GetAllWikiGroupsAction({}));
        },
        onFailure: error => {
          this.isLoading = false;
          console.error('Error deleting wiki group:', error);
        },
      })
    );
  }
}
